import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useFieldArray, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { toast } from 'sonner'
import { AlertTriangle, BarChart3, CircleStop, GripVertical, MoreHorizontal, Pencil, Plus, Send, Trash2, Undo2, X } from 'lucide-react'

import { PollResults } from '@/components/polls/poll-results'
import { MarkdownContent } from '@/components/markdown-content'
import { RichTextEditor } from '@/components/rich-text-editor'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { formatDateTime, fromInputDateTime, toInputDateTime } from '@/lib/date'
import { getErrorMessage } from '@/lib/error-messages'
import { markdownToPlainText } from '@/lib/markdown'
import {
  adminGetPollResults,
  adminListPolls,
  closePoll,
  deletePoll,
  publishPoll,
  savePoll,
  withdrawPoll,
  type AdminPoll,
  type PollDraft,
  type PollTargetRole,
} from '@/lib/polls-api'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'

const roleOptions: { value: PollTargetRole; label: string }[] = [
  { value: 'member', label: 'Teilnehmer' },
  { value: 'trainer', label: 'Trainer' },
]

const roleLabels: Record<PollTargetRole, string> = {
  member: 'Teilnehmer',
  trainer: 'Trainer',
}

const settingOptions = [
  {
    name: 'allow_multiple',
    label: 'Mehrfachauswahl',
    description: 'Teilnehmer können mehrere Antworten gleichzeitig wählen.',
  },
  {
    name: 'allow_participant_options',
    label: 'Eigene Antworten erlauben',
    description: 'Teilnehmer können weitere Antwortmöglichkeiten ergänzen.',
  },
  {
    name: 'results_visible',
    label: 'Ergebnisse für Teilnehmer sichtbar',
    description: 'Nach der eigenen Stimme bzw. nach Ende der Umfrage.',
  },
] as const

const pollSchema = z
  .object({
    title: z.string().trim().min(1, 'Titel ist erforderlich'),
    body: z.string().trim().min(1, 'Text ist erforderlich'),
    options: z.array(z.object({ id: z.string().nullable(), label: z.string() })),
    target_roles: z.array(z.enum(['member', 'trainer'])).min(1, 'Mindestens eine Zielgruppe wählen'),
    allow_multiple: z.boolean(),
    allow_participant_options: z.boolean(),
    results_visible: z.boolean(),
    visible_from: z.string(),
    visible_until: z.string(),
  })
  .superRefine((values, ctx) => {
    const labels = values.options.map((option) => option.label.trim())
    labels.forEach((label, index) => {
      const message = !label
        ? 'Antwort eingeben oder entfernen'
        : label.length > 200
          ? 'Höchstens 200 Zeichen'
          : labels.findIndex((other) => other.toLowerCase() === label.toLowerCase()) !== index
            ? 'Diese Antwort gibt es bereits'
            : null
      if (message) ctx.addIssue({ code: 'custom', message, path: ['options', index, 'label'] })
    })
    if (!values.allow_participant_options && labels.length < 2) {
      ctx.addIssue({
        code: 'custom',
        message: 'Mindestens zwei Antwortmöglichkeiten angeben oder eigene Antworten erlauben',
        path: ['options'],
      })
    }
    if (
      values.visible_from &&
      values.visible_until &&
      new Date(values.visible_until) <= new Date(values.visible_from)
    ) {
      ctx.addIssue({ code: 'custom', message: 'Muss nach „Teilnahme ab“ liegen', path: ['visible_until'] })
    }
  })

type PollFormValues = z.infer<typeof pollSchema>

const emptyForm: PollFormValues = {
  title: '',
  body: '',
  options: [
    { id: null, label: '' },
    { id: null, label: '' },
  ],
  target_roles: ['member'],
  allow_multiple: false,
  allow_participant_options: false,
  results_visible: false,
  visible_from: '',
  visible_until: '',
}

type DisplayStatus = 'draft' | 'scheduled' | 'active' | 'ended' | 'withdrawn'

function getDisplayStatus(poll: AdminPoll, now = new Date()): DisplayStatus {
  if (poll.status === 'draft') return 'draft'
  if (poll.status === 'withdrawn') return 'withdrawn'
  if (poll.visible_until && new Date(poll.visible_until) <= now) return 'ended'
  if (poll.visible_from && new Date(poll.visible_from) > now) return 'scheduled'
  return 'active'
}

const statusBadges: Record<DisplayStatus, { label: string; className: string }> = {
  draft: { label: 'Entwurf', className: 'bg-muted text-muted-foreground' },
  scheduled: { label: 'Geplant', className: 'bg-amber-500/15 text-amber-600 dark:text-amber-400' },
  active: { label: 'Aktiv', className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' },
  ended: { label: 'Beendet', className: 'bg-muted text-muted-foreground' },
  withdrawn: { label: 'Zurückgezogen', className: 'bg-destructive/15 text-destructive' },
}

type PendingAction = { type: 'publish' | 'withdraw' | 'close' | 'delete'; poll: AdminPoll }

const pendingActionTexts: Record<
  PendingAction['type'],
  { title: string; confirm: string; success: string; description: (poll: AdminPoll) => string }
> = {
  publish: {
    title: 'Umfrage veröffentlichen?',
    confirm: 'Veröffentlichen',
    success: 'Umfrage veröffentlicht.',
    description: (poll) =>
      poll.visible_from && new Date(poll.visible_from) > new Date()
        ? `„${poll.title}“ startet am ${formatDateTime(poll.visible_from)}.`
        : `„${poll.title}“ wird sofort allen Benutzern der Zielgruppe angezeigt.`,
  },
  withdraw: {
    title: 'Umfrage zurückziehen?',
    confirm: 'Zurückziehen',
    success: 'Umfrage zurückgezogen.',
    description: (poll) =>
      `„${poll.title}“ wird für Teilnehmer ausgeblendet. Bisherige Stimmen bleiben erhalten.`,
  },
  close: {
    title: 'Umfrage jetzt beenden?',
    confirm: 'Beenden',
    success: 'Umfrage beendet.',
    description: (poll) =>
      `Für „${poll.title}“ kann nicht mehr abgestimmt werden. Sie bleibt auf der Umfragen-Seite sichtbar.`,
  },
  delete: {
    title: 'Umfrage löschen?',
    confirm: 'Löschen',
    success: 'Umfrage gelöscht.',
    description: (poll) => `„${poll.title}“ wird inklusive aller Stimmen endgültig gelöscht.`,
  },
}

export function AdminPollsPage() {
  const queryClient = useQueryClient()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editing, setEditing] = useState<AdminPoll | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  const [resultsFor, setResultsFor] = useState<AdminPoll | null>(null)

  const pollsQuery = useQuery({
    queryKey: queryKeys.adminPolls,
    queryFn: adminListPolls,
  })

  const form = useForm<PollFormValues>({
    resolver: zodResolver(pollSchema),
    defaultValues: emptyForm,
  })
  // keyName avoids clashing with the option ids stored in the field values.
  const optionFields = useFieldArray({ control: form.control, name: 'options', keyName: 'fieldKey' })
  const optionsError = form.formState.errors.options
  // Votes and authors of the stored options, shown next to them while editing.
  const storedOptions = new Map((editing?.options ?? []).map((option) => [option.id, option]))
  // Answers are reordered by dragging their handle; the row only becomes
  // draggable while the handle is pressed, so the inputs stay editable.
  const [dragHandleIndex, setDragHandleIndex] = useState<number | null>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const resetDrag = () => {
    setDragHandleIndex(null)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  // Prefix also covers the results of each poll.
  const invalidate = () => queryClient.invalidateQueries({ queryKey: queryKeys.adminPolls })

  const saveMutation = useMutation({
    mutationFn: (values: PollFormValues) => {
      const draft: PollDraft = {
        title: values.title.trim(),
        body: values.body.trim(),
        target_roles: values.target_roles,
        allow_multiple: values.allow_multiple,
        allow_participant_options: values.allow_participant_options,
        results_visible: values.results_visible,
        visible_from: fromInputDateTime(values.visible_from),
        visible_until: fromInputDateTime(values.visible_until),
        options: values.options.map((option) => ({ id: option.id, label: option.label.trim() })),
      }
      return savePoll(editing?.id ?? null, draft)
    },
    onSuccess: () => {
      invalidate()
      toast.success(editing ? 'Umfrage aktualisiert.' : 'Entwurf erstellt.')
      setIsFormOpen(false)
      setEditing(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const actionMutation = useMutation({
    mutationFn: ({ type, poll }: PendingAction) => {
      if (type === 'publish') return publishPoll(poll.id)
      if (type === 'withdraw') return withdrawPoll(poll.id)
      if (type === 'close') return closePoll(poll.id)
      return deletePoll(poll.id)
    },
    onSuccess: (_, { type }) => {
      invalidate()
      toast.success(pendingActionTexts[type].success)
      setPendingAction(null)
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setIsFormOpen(true)
  }

  const openEdit = (poll: AdminPoll) => {
    setEditing(poll)
    form.reset({
      title: poll.title,
      body: poll.body,
      options: poll.options.map((option) => ({ id: option.id, label: option.label })),
      target_roles: poll.target_roles,
      allow_multiple: poll.allow_multiple,
      allow_participant_options: poll.allow_participant_options,
      results_visible: poll.results_visible,
      visible_from: toInputDateTime(poll.visible_from),
      visible_until: toInputDateTime(poll.visible_until),
    })
    setIsFormOpen(true)
  }

  const polls = pollsQuery.data ?? []

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold">Umfragen</h2>
          <p className="text-sm text-muted-foreground">
            Offene Umfragen werden beim nächsten Aufruf der App angezeigt, bis abgestimmt oder geschlossen wird.
          </p>
        </div>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" />
          Neue Umfrage
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {pollsQuery.isLoading ? (
            <div className="space-y-2 p-6">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-8 w-full" />
            </div>
          ) : pollsQuery.isError ? (
            <div className="flex flex-col items-center gap-3 p-8 text-center">
              <p className="text-sm text-destructive">{getErrorMessage(pollsQuery.error)}</p>
              <Button variant="outline" size="sm" onClick={() => pollsQuery.refetch()}>
                Erneut laden
              </Button>
            </div>
          ) : polls.length === 0 ? (
            <p className="p-8 text-center text-sm text-muted-foreground">Noch keine Umfragen vorhanden.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Titel</TableHead>
                  <TableHead>Zielgruppe</TableHead>
                  <TableHead>Einstellungen</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Zeitraum</TableHead>
                  <TableHead className="text-right">Beteiligung</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {polls.map((poll) => {
                  const status = getDisplayStatus(poll)
                  const badge = statusBadges[status]
                  const percent =
                    poll.target_count > 0 ? Math.round((poll.voter_count / poll.target_count) * 100) : 0
                  const editable = poll.status !== 'published'

                  return (
                    <TableRow key={poll.id}>
                      <TableCell className="max-w-xs">
                        <button
                          type="button"
                          className="block w-full truncate text-left font-medium hover:underline"
                          onClick={() => setResultsFor(poll)}
                        >
                          {poll.title}
                        </button>
                        <div className="truncate text-xs text-muted-foreground">
                          {markdownToPlainText(poll.body)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {poll.target_roles.map((role) => (
                            <Badge key={role} variant="outline">
                              {roleLabels[role]}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          {poll.option_count} {poll.option_count === 1 ? 'Antwort' : 'Antworten'}
                        </div>
                        <div className="flex flex-wrap gap-1 pt-1">
                          {poll.allow_multiple && <Badge variant="outline">Mehrfach</Badge>}
                          {poll.allow_participant_options && <Badge variant="outline">Eigene Antworten</Badge>}
                          {poll.results_visible && <Badge variant="outline">Ergebnisse sichtbar</Badge>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary" className={badge.className}>
                          {badge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        <div>
                          {'ab '}
                          {poll.visible_from
                            ? formatDateTime(poll.visible_from)
                            : poll.published_at && poll.status === 'published'
                              ? formatDateTime(poll.published_at)
                              : 'Veröffentlichung'}
                        </div>
                        <div className="text-muted-foreground">
                          {poll.visible_until ? `bis ${formatDateTime(poll.visible_until)}` : 'unbegrenzt'}
                        </div>
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-right text-sm">
                        <div className="tabular-nums">
                          {poll.voter_count} / {poll.target_count}
                        </div>
                        <div className="text-xs text-muted-foreground">{percent}%</div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" aria-label="Aktionen">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onSelect={() => setResultsFor(poll)}>
                              <BarChart3 className="mr-2 h-4 w-4" />
                              Ergebnisse
                            </DropdownMenuItem>
                            <DropdownMenuItem onSelect={() => openEdit(poll)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Bearbeiten
                            </DropdownMenuItem>
                            {editable && (
                              <DropdownMenuItem onSelect={() => setPendingAction({ type: 'publish', poll })}>
                                <Send className="mr-2 h-4 w-4" />
                                {poll.status === 'withdrawn' ? 'Erneut veröffentlichen' : 'Veröffentlichen'}
                              </DropdownMenuItem>
                            )}
                            {status === 'active' && (
                              <DropdownMenuItem onSelect={() => setPendingAction({ type: 'close', poll })}>
                                <CircleStop className="mr-2 h-4 w-4" />
                                Jetzt beenden
                              </DropdownMenuItem>
                            )}
                            {poll.status === 'published' && (
                              <DropdownMenuItem onSelect={() => setPendingAction({ type: 'withdraw', poll })}>
                                <Undo2 className="mr-2 h-4 w-4" />
                                Zurückziehen
                              </DropdownMenuItem>
                            )}
                            {editable && (
                              <>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => setPendingAction({ type: 'delete', poll })}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Löschen
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Umfrage bearbeiten' : 'Neue Umfrage'}</DialogTitle>
            <DialogDescription>
              {editing?.status === 'published'
                ? 'Die Umfrage ist veröffentlicht: Änderungen gelten sofort für alle Teilnehmer.'
                : editing
                  ? 'Änderungen werden erst nach dem Veröffentlichen angezeigt.'
                  : 'Wird als Entwurf gespeichert und erst nach dem Veröffentlichen angezeigt.'}
            </DialogDescription>
          </DialogHeader>
          {editing && editing.voter_count > 0 && (
            <Alert className="border-amber-500/40 bg-amber-500/10 text-amber-800 dark:text-amber-300 [&>svg]:text-amber-600 dark:[&>svg]:text-amber-400">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                {`Es haben bereits ${editing.voter_count} von ${editing.target_count} abgestimmt. `}
                Umbenannte Antworten behalten ihre Stimmen, entfernte Antworten verlieren sie.
              </AlertDescription>
            </Alert>
          )}
          <Form {...form}>
            <form onSubmit={form.handleSubmit((values) => saveMutation.mutate(values))} className="space-y-4">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Titel</FormLabel>
                    <FormControl>
                      <Input placeholder="z. B. Neue Kurszeiten" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Text</FormLabel>
                    <FormControl>
                      <RichTextEditor value={field.value} onChange={field.onChange} onBlur={field.onBlur} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-2">
                <Label>Antwortmöglichkeiten</Label>
                {optionFields.fields.map((optionField, index) => {
                  const stored = optionField.id ? storedOptions.get(optionField.id) : undefined
                  return (
                    <FormField
                      key={optionField.fieldKey}
                      control={form.control}
                      name={`options.${index}.label`}
                      render={({ field }) => (
                        <FormItem
                          draggable={dragHandleIndex === index}
                          onDragStart={(event) => {
                            event.dataTransfer.effectAllowed = 'move'
                            event.dataTransfer.setData('text/plain', String(index))
                            setDraggedIndex(index)
                          }}
                          onDragOver={(event) => {
                            if (draggedIndex === null) return
                            event.preventDefault()
                            event.dataTransfer.dropEffect = 'move'
                            setDragOverIndex(index)
                          }}
                          onDrop={(event) => {
                            event.preventDefault()
                            if (draggedIndex !== null && draggedIndex !== index) {
                              optionFields.move(draggedIndex, index)
                            }
                            resetDrag()
                          }}
                          onDragEnd={resetDrag}
                          className={cn(
                            'rounded-md',
                            draggedIndex === index
                              ? 'opacity-50'
                              : draggedIndex !== null && dragOverIndex === index && 'bg-muted/70',
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span
                              title="Ziehen, um die Reihenfolge zu ändern."
                              aria-hidden="true"
                              className="inline-flex cursor-grab text-muted-foreground active:cursor-grabbing"
                              onMouseDown={() => setDragHandleIndex(index)}
                              onMouseUp={() => setDragHandleIndex(null)}
                            >
                              <GripVertical className="h-5 w-5" />
                            </span>
                            <FormControl>
                              <Input placeholder={`Antwort ${index + 1}`} maxLength={200} {...field} />
                            </FormControl>
                            {stored && (
                              <Badge
                                variant={stored.vote_count > 0 ? 'secondary' : 'outline'}
                                className="shrink-0 tabular-nums"
                              >
                                {stored.vote_count === 1 ? '1 Stimme' : `${stored.vote_count} Stimmen`}
                              </Badge>
                            )}
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Antwort ${index + 1} entfernen`}
                              onClick={() => optionFields.remove(index)}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                          {stored?.added_by_participant && (
                            <FormDescription>
                              Eingebracht von {stored.author_name ?? 'gelöschtem Benutzer'}
                            </FormDescription>
                          )}
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )
                })}
                {optionsError?.message && (
                  <p className="text-sm font-medium text-destructive">{optionsError.message}</p>
                )}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => optionFields.append({ id: null, label: '' })}
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Antwort hinzufügen
                </Button>
              </div>
              <FormField
                control={form.control}
                name="target_roles"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Zielgruppe</FormLabel>
                    <div className="flex flex-wrap gap-4">
                      {roleOptions.map((option) => (
                        <label key={option.value} className="flex items-center gap-2 text-sm">
                          <Checkbox
                            checked={field.value.includes(option.value)}
                            onCheckedChange={(checked) =>
                              field.onChange(
                                checked === true
                                  ? [...field.value, option.value]
                                  : field.value.filter((role) => role !== option.value),
                              )
                            }
                          />
                          {option.label}
                        </label>
                      ))}
                    </div>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="space-y-3">
                <Label>Einstellungen</Label>
                {settingOptions.map((setting) => (
                  <FormField
                    key={setting.name}
                    control={form.control}
                    name={setting.name}
                    render={({ field }) => (
                      <FormItem className="flex items-start gap-3 space-y-0">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={(checked) => field.onChange(checked === true)}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel className="font-normal">{setting.label}</FormLabel>
                          <FormDescription>{setting.description}</FormDescription>
                        </div>
                      </FormItem>
                    )}
                  />
                ))}
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <FormField
                  control={form.control}
                  name="visible_from"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teilnahme ab (optional)</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormDescription>Leer = sofort bei Veröffentlichung.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="visible_until"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Teilnahme bis (optional)</FormLabel>
                      <FormControl>
                        <Input type="datetime-local" {...field} />
                      </FormControl>
                      <FormDescription>Leer = unbegrenzt.</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => setIsFormOpen(false)}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={saveMutation.isPending}>
                  {saveMutation.isPending ? 'Speichert...' : editing ? 'Speichern' : 'Entwurf speichern'}
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      <Dialog open={pendingAction !== null} onOpenChange={(open) => !open && setPendingAction(null)}>
        <DialogContent>
          {pendingAction && (
            <>
              <DialogHeader>
                <DialogTitle>{pendingActionTexts[pendingAction.type].title}</DialogTitle>
                <DialogDescription>
                  {pendingActionTexts[pendingAction.type].description(pendingAction.poll)}
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setPendingAction(null)}>
                  Abbrechen
                </Button>
                <Button
                  variant={pendingAction.type === 'publish' ? 'default' : 'destructive'}
                  disabled={actionMutation.isPending}
                  onClick={() => actionMutation.mutate(pendingAction)}
                >
                  {pendingActionTexts[pendingAction.type].confirm}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ResultsDialog poll={resultsFor} onClose={() => setResultsFor(null)} />
    </div>
  )
}

function ResultsDialog({ poll, onClose }: { poll: AdminPoll | null; onClose: () => void }) {
  const resultsQuery = useQuery({
    queryKey: queryKeys.pollResults(poll?.id ?? ''),
    queryFn: () => adminGetPollResults(poll!.id),
    enabled: poll !== null,
  })

  return (
    <Dialog open={poll !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        {poll && (
          <>
            <DialogHeader>
              <DialogTitle>{poll.title}</DialogTitle>
              <DialogDescription>
                {`${poll.voter_count} von ${poll.target_count} haben abgestimmt`}
                {poll.allow_multiple && ' · Mehrfachauswahl, Prozent bezogen auf Abstimmende'}
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-48 overflow-y-auto rounded-md border p-3">
              <MarkdownContent>{poll.body}</MarkdownContent>
            </div>
            {resultsQuery.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : resultsQuery.isError ? (
              <p className="text-sm text-destructive">{getErrorMessage(resultsQuery.error)}</p>
            ) : (
              <PollResults
                voterCount={poll.voter_count}
                rows={(resultsQuery.data ?? []).map((option) => ({
                  id: option.option_id,
                  label: option.option_label,
                  votes: option.vote_count,
                  author: option.author_name ?? (option.added_by_participant ? 'gelöschtem Benutzer' : null),
                }))}
              />
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Schließen
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
